import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { t } from '../../i18n';
import { docNumber } from '../lib/docNumber';
import styles from './ui.module.css';

/**
 * Название экрана в шапке: Unbounded, широкий разряд, сбитый RGB.
 *
 * Отдаётся внутрь <PanelHeader> — сам PanelHeader остаётся вкуишный, потому
 * что он держит место под кнопки ВК справа и безопасные отступы сверху.
 *
 * Разряд и аберрация живут ТОЛЬКО здесь и на бренде. В теле текста их быть не
 * должно: анкета — четырнадцать полей прозы, и читать её разряженным глитчем
 * невозможно.
 *
 * `count` — сколько всего в списке. В макетах это число стоит в правом краю
 * шапки, и отдавать его в `after` у PanelHeader нельзя: при
 * hasCustomPanelHeaderAfter (а в мини-приложении это всегда так) VKUI
 * ВЫБРАСЫВАЕТ содержимое слота и оставляет пустую распорку под кнопки ВК —
 * проверено в браузере, слот приезжал пустым с min-width: 90px. Поэтому счёт
 * живёт внутри содержимого шапки, слева от этой распорки.
 */
export function ScreenTitle({
  children,
  count,
  unit,
  number,
  after,
  loud = false,
}: {
  children: string;
  count?: number | null;
  /**
   * ЧТО считаем: ключ единицы в словаре. Без него в шапке висит голое число,
   * которое ничего не говорит, — а со словом его ещё и склонять надо, и это
   * забота словаря, а не экрана. Залитая плашка (`loud`) слова не берёт: там
   * число и есть повод открыть экран, и читается оно значком.
   */
  unit?: string;
  /**
   * НОМЕР, а не счёт: там, где списка нет вовсе — экран своего персонажа.
   * Набирается как всякий номер в проекте, четырьмя знаками с ведущими
   * нулями; счёт так набирать нельзя, «0016 решений» это не число вещей.
   */
  number?: number | null;
  /**
   * Что стоит в правом краю шапки вместо числа: значок состояния заявки на
   * экране разбора. В макетах он именно там — разбор открывают, уже зная, что
   * проверяют, и первым делом смотрят, решена ли заявка.
   */
  after?: ReactNode;
  /** Залитая плашка вместо цифры: там, где число и есть повод открыть экран. */
  loud?: boolean;
}) {
  const value =
    number !== undefined && number !== null
      ? docNumber(number)
      : count === undefined || count === null
        ? null
        : unit === undefined || loud
          ? count
          : t(`count.${unit}`, { count });

  const tail =
    after ??
    (value === null ? null : (
      <span className={clsx(styles.screenCount, loud && styles.screenCountLoud)}>{value}</span>
    ));

  if (tail === null) return <span className={styles.screenTitle}>{children}</span>;

  return (
    <span className={styles.screenTitleRow}>
      <span className={styles.screenTitle}>{children}</span>
      <span className={styles.screenTitleTail}>{tail}</span>
    </span>
  );
}
